import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProjectProvider, useProject } from './context/ProjectContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { DataVersionProvider } from './context/DataVersionContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import RedefinirSenha from './pages/RedefinirSenha';
import Aderir from './pages/Aderir';
import Dashboard from './pages/Dashboard';
import ResourceListPage from './pages/ResourceListPage';
import Transactions from './pages/Transactions';
import ProjectsManagement from './pages/ProjectsManagement';
import PayableReceivable from './pages/PayableReceivable';
import Distribution from './pages/Distribution';
import Profile from './pages/Profile';
import ResourceTransactions from './pages/ResourceTransactions';
import TagsPage from './pages/TagsPage';
import Admin from './pages/Admin';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-300">Carregando...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;
  return children;
}

function ProjectGuard({ children }) {
  const { currentProject, projects, loading } = useProject();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500 dark:text-slate-400">Carregando projeto...</div>;
  const onMgmt = location.pathname.startsWith('/projects/manage') || location.pathname === '/profile';
  if (!projects.length && !onMgmt) {
    return (
      <Layout>
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 text-center">
          <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">Nenhum projeto encontrado</h3>
          <p className="text-slate-500 dark:text-slate-400 mb-4">Crie um projeto para começar a usar o D'Pots.</p>
          <a href="/projects/manage" className="inline-block px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg">Criar projeto</a>
        </div>
      </Layout>
    );
  }
  if (!currentProject && !onMgmt) return null;
  return children;
}

function AppToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" richColors theme={theme} closeButton />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Rota pública: quem chega aqui, por definição, não consegue entrar. */}
      <Route path="/redefinir-senha" element={<RedefinirSenha />} />
      <Route path="/aderir" element={<Aderir />} />
      <Route path="/*" element={
        <ProtectedRoute>
          <ProjectProvider>
            <DataVersionProvider>
              <ProjectGuard>
                <Layout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/transactions" element={<Transactions />} />
                    <Route path="/pots" element={<ResourceListPage resource="pots" title="Potes" withBalance extraFields={['credit_card']} linkTo="/pots" />} />
                    <Route path="/pots/:id/transactions" element={<ResourceTransactions kind="pots" />} />
                    <Route path="/accounts" element={<ResourceListPage resource="accounts" title="Contas" withBalance linkTo="/accounts" />} />
                    <Route path="/accounts/:id/transactions" element={<ResourceTransactions kind="accounts" />} />
                    <Route path="/revenue-sources" element={<ResourceListPage resource="revenue_sources" title="Fontes de Receita" withBalance={false} linkTo="/revenue-sources" />} />
                    <Route path="/revenue-sources/:id/transactions" element={<ResourceTransactions kind="revenue-sources" />} />
                    <Route path="/entities" element={<ResourceListPage resource="entities" title="Pessoas/Entidades" withBalance={false} extraFields={['email','phone']} linkTo="/entities" />} />
                    <Route path="/entities/:id/transactions" element={<ResourceTransactions kind="entities" />} />
                    <Route path="/tags" element={<TagsPage />} />
                    <Route path="/distribution" element={<Distribution />} />
                    <Route path="/payable-receivable" element={<PayableReceivable />} />
                    <Route path="/projects/manage" element={<ProjectsManagement />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/admin" element={<Admin />} />
                  </Routes>
                </Layout>
              </ProjectGuard>
            </DataVersionProvider>
          </ProjectProvider>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </BrowserRouter>
          <AppToaster />
        </ConfirmProvider>
      </ThemeProvider>
    </div>
  );
}
